import React from "react";
import { Shield, Globe, Users, Award, CheckCircle2, TrendingUp } from "lucide-react";

export default function About() {
  return (
    <div className="text-white">
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-20 relative">
          <div className="eyebrow mb-6">Corporate Profile</div>
          <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
            One holding company.<br />
            Eight specialist divisions.<br />
            <span className="text-[#FF5722]">A hundred hands offshore.</span>
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">
            Billtech Holdings is a diversified holding group structured to
            deliver the depth of a boutique specialist and the scale of an integrated conglomerate.
            Its Marine &amp; Engineering Group commands eight operating divisions and 100+
            micro-subsidiaries across offshore energy, subsea engineering, marine logistics,
            asset integrity and decommissioning — with each entity carrying its own
            leadership, its own liability perimeter, and its own operational reputation,
            all backed by the parent company&rsquo;s capital, QHSE machinery, and centralized
            human resources.
          </p>
        </div>
      </section>

      {/* OUR MODEL */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">The Model</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[0.95]">
              Shared services. Micro-branded execution.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-white/80 leading-relaxed">
            <p>
              Every Billtech technician, engineer, and mariner is contracted centrally by the parent
              holding company under a Deployment &amp; Secondment clause. Under this shared-services
              model, personnel can be repositioned across subsidiaries or offshore projects
              instantly, with intercompany invoicing preserving clean tax and accounting boundaries.
            </p>
            <p>
              This structure lets us mobilize a Digital Twin engineer, a Cathodic Protection
              designer, and an Offshore Medical crew to the same platform inside the same window —
              coordinated by a single project office, executed by three distinct legal entities.
            </p>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-y border-white/10 bg-[#0C1322]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="eyebrow mb-4">Operating Principles</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[0.95] mb-14 max-w-3xl">
            Uncompromising standards, no matter which flag flies over the deck.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Principle
              icon={<Shield size={20} />}
              title="Unified QHSE"
              body="A single Quality, Health, Safety & Environment doctrine governs every subsidiary. Target-zero incident record maintained across all operating regions."
            />
            <Principle
              icon={<Globe size={20} />}
              title="Global Mobilization"
              body="24/7 operations centers in Houston, Aberdeen, Lagos and Singapore. Personnel, spares and vessels move across jurisdictions inside a unified operational framework."
            />
            <Principle
              icon={<Users size={20} />}
              title="Centralized Talent"
              body="One recruitment engine, one training academy, one medical infrastructure — deployable across every subsidiary under the group umbrella."
            />
            <Principle
              icon={<Award size={20} />}
              title="Niche Authority"
              body="Boutique subsidiary brands compete for niche IOC contracts on their own technical merit — while carrying the group balance sheet behind them."
            />
            <Principle
              icon={<CheckCircle2 size={20} />}
              title="Liability Isolation"
              body="Each subsidiary is an independent legal entity. Risk stays contained to the entity that executed the specific scope — shielding the group at large."
            />
            <Principle
              icon={<TrendingUp size={20} />}
              title="Commercial Modularity"
              body="Any subsidiary can be independently invested, spun-off or partnered — offering IOCs and financial sponsors a clean interface for targeted deals."
            />
          </div>
        </div>
      </section>

      {/* HR / TRAINING */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">People &amp; Training</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[0.95]">
              The Group&rsquo;s greatest asset walks the deck.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed">
              Centralized HR handles psychological screening, technical certification and continuous
              training for every deployable resource in the group.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-white/10 border border-white/10 bg-[#0C1322]">
              {[
                ["Offshore Psychometric Testing", "Screening for high-stress, isolated environments."],
                ["Simulated Drilling Training", "VR simulators for blowout-prevention drill scenarios."],
                ["Emergency Evacuation Planning", "Escape-route protocols for catastrophic failures."],
                ["Telemedicine Infrastructure", "Live video links between rig medics and onshore hospitals."],
                ["Occupational Hygiene Auditing", "Air quality and ergonomics testing in working spaces."],
              ].map(([t, b]) => (
                <li key={t} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-6">
                  <div className="md:col-span-4 font-display font-bold text-white">{t}</div>
                  <div className="md:col-span-8 text-sm text-white/70 leading-relaxed">{b}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

function Principle({ icon, title, body }) {
  return (
    <div className="border border-white/10 bg-[#060A14] p-7 hover:border-[#FF5722] transition-colors">
      <div className="w-10 h-10 bg-[#131D33] text-[#FF5722] flex items-center justify-center mb-5">
        {icon}
      </div>
      <div className="font-display text-xl font-bold mb-3">{title}</div>
      <div className="text-sm text-white/70 leading-relaxed">{body}</div>
    </div>
  );
}
